import React from "react"
import PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { Link } from "gatsby"
import { breakpoints } from "../../../theme"
import { buttonStyle, Avatar, CmsImage } from "@bw/bits"
import useGlobalTheme from "hooks/useGlobalTheme"

const TeaserStyle = css`
  text-decoration: none;
  width: 100%;
  display: block;
  color: var(--teaser-color, #111);
  cursor: pointer;
  position: relative;
`

const StyledLink = styled(Link)`
  ${TeaserStyle}
`

const StyledA = styled.a`
  ${TeaserStyle}
`

const StyledDiv = styled.div`
  ${TeaserStyle}
`

const TeaserWrapper = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 6px;
  background-color: var(--teaser-background-color, #fff);
  display: flex;
  flex-direction: column; 
  justify-content: flex-start;
  box-shadow: none;
  margin: 0 15px 0 15px;
`

const Background = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;

  .gatsby-image-wrapper{
    width: 100%;
    height: auto;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: cover;
    display: block;
  }
`

const Small = styled.div`
  font-size: 12px;
  line-height: 165%;
`

const Title = styled.div`
  font-weight: 500;
  font-size: 18px;
  line-height: 1.2;
  margin: 0 0 0.1em;

  &:first-child {
    margin-top: 8px;

    @media (min-width: ${breakpoints.small}px) {
      margin-top: 0;
    }
  }
`

const Excerpt = styled.div`
  font-size: 14px;
  line-height: 1.4;
  margin: 0.4em 0;
`

const TeaserBody = styled.div`
  padding: 2px 10px 10px;
  background: #fff;
  color: #000;

`

const TopMargin = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 30px;
  display: flex;
  font-weight: bold;
  font-size: 13px;
  color: white;
  align-items: start;
  padding: 20px 20px 0 20px;

  a {
    color: var(--hero-color);
  }
`

const Button = styled.div`
  ${buttonStyle}
  margin: 25px 0 0 0;
`

const Image = styled.div`
  > * {
    width: 80px;
    height: 80px;

    @media (min-width: ${breakpoints.small}px) {
      width: 100%;
      height: 150px;
    }
  }
`

const Category = styled.div`
  padding: 5px 15px;
  border-radius: 38px;
  background-color: ${props => props.categoryColor};
`

const Teaser = ({
  suptitle,
  title,
  excerpt,
  to,
  href,
  onClick,
  image,
  category,
  categoryColor,
  buttonLabel,
  author,
}) => {
  const theme = useGlobalTheme()

  if (!image && theme.defaultTeaserImage?.localImage?.childImageSharp?.original?.src) {
    image = <CmsImage src={theme.defaultTeaserImage?.localImage?.childImageSharp?.original?.src} alt={title || excerpt} />
  }

  const Card = () => (
    <TeaserWrapper className="teaser">
      <Background className={`teaser__image-wrapper`}>
        <Image className={`teaser__image`}>{image}</Image>
        <TopMargin className={`teaser__topmargin`}>
          <Category
            categoryColor={categoryColor}
            className={`teaser__category`}
          >
            {category}
          </Category>
        </TopMargin>
      </Background>
      <TeaserBody className={`teaser__body`}>
        {suptitle && <Small className={`teaser__suptitle`}>{suptitle}</Small>}
        {title && <Title className={`teaser__title`}>{title}</Title>}
        {excerpt && <Excerpt className={`teaser__excerpt`}>{excerpt}</Excerpt>}
        {buttonLabel && (
          <Button $fill $primary>
            {buttonLabel}
          </Button>
        )}
        {author && <Avatar name={author.name} image={author.image} />}
      </TeaserBody>
    </TeaserWrapper>
  )

  switch (true) {
    case !!to:
      return (
        <StyledLink to={to}>
          <Card />
        </StyledLink>
      )

    case !!href:
      return (
        <StyledA href={href} target="_blank">
          <Card />
        </StyledA>
      )

    case !!onClick:
      return (
        <StyledDiv onClick={onClick}>
          <Card />
        </StyledDiv>
      )

    default:
      throw new Error(
        "Unsupported type of teaser. Should use one of these props: to, href or onClick"
      )
  }
}

export { Teaser }

Teaser.propTypes = {
  image: PropTypes.element,
  suptitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
  buttonLabel: PropTypes.string,
  onClick: PropTypes.func,
  href: PropTypes.string,
  to: PropTypes.string,
  category: PropTypes.string,
  categoryColor: PropTypes.string,
  sideContent: PropTypes.string,
  author: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.any,
  }),
}
